import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { checkTableName, type TableReservationNotice } from "../../../../../api/bills";
import { ReservationConfirmModal } from "./ReservationConfirmModal";
import { findFreeTableName } from "./nextFreeTableName";
import { reservationToConfirm, tableReservationNoticeText } from "./tableReservationNotice";

interface PendingConfirm {
  /** Целият текст в модала. */
  text: string;
  /** Етикетът на основния бутон („Продължи" / „Отвори маса 4"); `null` —
   *  само отказ (другата маса на настанена резервация). */
  continueLabel: string | null;
  resolve: (proceed: boolean) => void;
}

/**
 * Резултатът от `offerFreeTable`: `open` — човекът прие предложената маса;
 * `declined` — „Отказ" на предложението; `none` — няма свободен номер без
 * резервация (нечислово име, 20 опита или облакът не отговори).
 */
export type FreeTableOffer =
  | { kind: "open"; name: string }
  | { kind: "declined" }
  | { kind: "none" };

/**
 * Питането „масата има резервация — продължаваш ли?" за всеки поток в
 * Маркиране, който отваря или сменя маса по име. Връща обещание: `true` =
 * „Продължи" (или няма какво да се пита), `false` = „Отказ".
 *
 * - `confirmNotice` — когато проверката на името вече е направена;
 * - `confirmTable` — сам пита `GET /bills/check-table-name`. Провалена
 *   проверка НЕ спира действието (при съмнение обслужвай) — питането е удобство.
 * - `offerFreeTable` — след „Отказ" при БЪРЗОТО отваряне: намира следващия
 *   свободен номер без резервация (`nextFreeTableName.ts`, до 20 проверки
 *   `check-table-name`) и пита в СЪЩИЯ модал „Маса 3 е резервирана. Да отворим
 *   ли маса 4?". `afterSearch` се вика щом проверките свършат — ПРЕДИ модала
 *   или резултата (извикващият маха loading toast-а). Съобщението „няма
 *   свободна маса" е при извикващия.
 *
 * Модалът (`reservationConfirmModal`) се рендерира от извикващия.
 */
export const useReservationConfirm = () => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);
  pendingRef.current = pending;
  // Последното питане остава в модала, докато той изчезва (анимацията).
  const lastShownRef = useRef<PendingConfirm | null>(null);
  if (pending) lastShownRef.current = pending;
  const shown = pending ?? lastShownRef.current;

  // Затворен екран с висящо питане = „Отказ" (обещанието не остава да виси).
  useEffect(() => () => pendingRef.current?.resolve(false), []);

  const ask = useCallback(
    (text: string, continueLabel: string | null) =>
      new Promise<boolean>((resolve) => {
        pendingRef.current?.resolve(false);
        setPending({ text, continueLabel, resolve });
      }),
    [],
  );

  const confirmNotice = useCallback(
    (tableName: string, notice: TableReservationNotice) =>
      // Другата маса на настанена резервация — не се отваря (BE също отказва):
      // само съобщение, „Разбрах" = „Отказ".
      notice.billTableName
        ? ask(
            t("ordering.reservationNotice.linkedTable", {
              table: tableName,
              guest: notice.guestName,
              billTable: notice.billTableName,
            }),
            null,
          )
        : ask(
        t("ordering.reservationNotice.question", {
          notice: tableReservationNoticeText(tableName, notice, t),
        }),
        t("ordering.reservationNotice.continue"),
      ),
    [ask, t],
  );

  const confirmTable = useCallback(
    async (tableName: string) => {
      let notice: TableReservationNotice | null;
      try {
        notice = reservationToConfirm(await checkTableName(tableName));
      } catch {
        return true;
      }
      return notice ? confirmNotice(tableName, notice) : true;
    },
    [confirmNotice],
  );

  const offerFreeTable = useCallback(
    async (reservedName: string, afterSearch?: () => void): Promise<FreeTableOffer> => {
      let free: string | null;
      try {
        free = await findFreeTableName(reservedName, checkTableName);
      } finally {
        afterSearch?.();
      }
      if (!free) return { kind: "none" };
      const proceed = await ask(
        t("ordering.reservationNotice.openInstead", { reserved: reservedName, free }),
        t("ordering.reservationNotice.openTable", { name: free }),
      );
      return proceed ? { kind: "open", name: free } : { kind: "declined" };
    },
    [ask, t],
  );

  const settle = (proceed: boolean) => {
    pending?.resolve(proceed);
    setPending(null);
  };

  const reservationConfirmModal = (
    <ReservationConfirmModal
      open={pending !== null}
      text={shown?.text ?? ""}
      continueLabel={shown ? shown.continueLabel : ""}
      onCancel={() => settle(false)}
      onContinue={() => settle(true)}
    />
  );

  return { confirmNotice, confirmTable, offerFreeTable, reservationConfirmModal };
};
